import { Injectable } from '@angular/core';
import { ProductsService } from './products.service';
import { DiscountsService } from './discounts.service';
import { TAXES, USER_ROLES, TAX_GROUP } from '../enums';
import { HasRolePipe } from '../pipes/has-role.pipe';
import { LoopBackAuth } from '../../assets/js/lb-sdk';
import { WITHHOLDING_TAX_TYPE } from '../enums/WITHHOLDING_TAX_TYPE';
import { WITHHOLDING_TAX_CODE } from '../enums/WITHHOLDING_TAX_CODE';

@Injectable({
  providedIn: 'root'
})
export class OrderingService {
  taxes: typeof TAXES = TAXES;
  USER_ROLES: typeof USER_ROLES = USER_ROLES;
  private _isCredit = false;
  constructor(
    private productService: ProductsService,
    private discountService: DiscountsService,
    private hasRole: HasRolePipe,
    private loopBackAuth: LoopBackAuth
  ) { }

  round(value, decimals = 2) {
    const parsed = parseFloat(Number(Math.round(Number(value + 'e4')) + 'e-4').toFixed(4));
    return parseFloat(Number(Math.round(Number(parsed + 'e' + decimals)) + 'e-' + decimals).toFixed(decimals));
  }

  public get isCredit(): boolean {
    return this._isCredit;
  }

  public set isCredit(value: boolean) {
    this._isCredit = value;
  }

  /**
   * orderingService.totalCWT(orders) && (getUser()?.roles | hasRole : USER_ROLES.GENERAL_MERCHANT : 'between' : USER_ROLES.AUH) || (getUser()?.roles | hasRole : USER_ROLES.BROKER)
   * @param orders 
   * @param roles 
   */
  hasCWT(orders) {
    return this.totalCWT(orders) && this.isMerchant();
  }

  isMerchant() {
    return this.hasRole.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, 'between', USER_ROLES.ONLINE_RETAILER) || this.hasRole.transform(this.getUser().roles, USER_ROLES.BROKER)
  }


  getOrderCount(orders: { [key:string]: number }, product):number {
    return orders[(product.productType || {}).name + product.planTypeCode] || 0;
  }

  getTotalOrders(orders: { [key:string]: number}):number {
    return Object.keys(orders || {})
      .reduce((count, product) => {
        return count + (orders[product] || 0);
      }, 0);
  }

  removeEmpty(orders: { [key:string]: number }):Array<string> {
    return Object.keys(orders || {})
      .filter(product => {
        return orders[product] >= 1;
      });
  }

  getProductOrders(orders: { [key:string]: number }):Array<any> {
    return this.removeEmpty(orders)
      .map(productTypeAndId => {
        return this.getProductByProdTypePlanCode(productTypeAndId);
      })
      .filter(product => {
        return !!product;
      })
  }

  getProductByProdTypePlanCode(prodTypePlanCode: string) {
    return (this.productService.getProducts() || [])
      .filter(product => {
        return ((product.productType || {}).name + product.planTypeCode) === prodTypePlanCode;
      })[0];
  }

  subTotal(orders: { [key:string]: number }):number {
    return this.round(Object.keys(orders || {})
      .reduce((total: number, prodTypePlanCode: string):number => {
        return this.productSubTotal(prodTypePlanCode, orders) + total;
      }, 0));
  }

  totalDiscount(orders: { [key:string]: number }):number {
    return this.round(Object.keys(orders).reduce((total: number, prodTypePlanCode: string):number => {
      const product = this.getProductByProdTypePlanCode(prodTypePlanCode);
      if (!product) {
        return total;
      }
      return total + this.productDiscountSubTotal(product, orders[prodTypePlanCode]);
    }, 0));
  }

  totalVolumeDiscount(orders: { [key:string]: number }):number {
    return this.round(Object.keys(orders).reduce((total: number, prodTypePlanCode: string):number => {
      const product = this.getProductByProdTypePlanCode(prodTypePlanCode);
      if (!product) {
        return total;
      }
      return total + this.productVolumeDiscountSubTotal(product, orders[prodTypePlanCode], orders);
    }, 0));
  }

  totalCWT(orders: { [key:string]: number }):number {
    if (this.isMerchant()) {
      return this.round(Object.keys(orders).reduce((total: number, prodTypePlanCode: string):number => {
        const product = this.getProductByProdTypePlanCode(prodTypePlanCode);
        if (!product) {
          return total;
        }
        const productCWTSubTotal = this.productCWTSubTotal(product, orders[prodTypePlanCode], orders);
        return total + productCWTSubTotal;
      }, 0));
    }
    return  0;
  }

  totalDiscountedAmount(orders: { [key:string]: number }) {
    return this.round((this.subTotal(orders) - this.totalDiscount(orders) - this.totalVolumeDiscount(orders)));
  }

  totalAmount(orders: { [key:string]: number }) {
    const totalCWT = this._isCredit ? 0 :  this.totalCWT(orders);
    return this.round((this.totalDiscountedAmount(orders) - totalCWT));
  }

  productSubTotal(product: any, orders?: { [key:string]: number }, quantity?: number): number {
    let productInstance = typeof product === 'string' ? this.getProductByProdTypePlanCode(product) : product;
    return this.round((productInstance ? (
      productInstance.taxedPrice * (quantity || (orders || {})[(productInstance.productType || {}).name + productInstance.planTypeCode] || 0)
    ) : 0));
  }

  productDiscountSubTotal(product, quantity: number): number {
    const discount = this.discountService.getDiscountByProduct(product);
    if (!discount) {
      return 0;
    }
    const rate = discount.rate ? this.round(discount.rate) : 0;
    switch((discount.unit || '').toUpperCase()) {
      case 'P':
        return this.round(this.round(quantity * (product.taxedPrice || 0)) * (rate/100));
      case 'C':
        return this.round((quantity * rate));
      default:
        return 0;
    }
  }

  productVolumeDiscountSubTotal(product, quantity: number, orders): number {
    const discount = this.discountService.getVolumeDiscountByProduct(product, orders);
    if (!discount) {
      return 0;
    }
    const rate = discount.discount ? this.round(discount.discount) : 0;
    switch((discount.unit || '').toUpperCase()) {
      case 'P':
        return this.round(this.round(quantity * (product.taxedPrice || 0)) * (rate/100));
      case 'C':
        return this.round((quantity * rate));
      default:
        return 0;
    }
  }

  productCWTSubTotal(product, quantity: number, orders):number {
    var cwtRate = this.getUser() && this.getUser().merchantDetails && this.getUser().merchantDetails.withholdingTaxType &&
      this.getUser().merchantDetails.withholdingTaxType === WITHHOLDING_TAX_TYPE.CUSTOMER_PAYMENT &&
      WITHHOLDING_TAX_CODE['W' + this.getUser().merchantDetails.withholdingTaxCode] || 0;
    var taxRate = this.getUser() && this.getUser().merchantDetails && this.getUser().merchantDetails.taxGroup &&
      TAX_GROUP[`T${ this.getUser().merchantDetails.taxGroup }`];
    if (this.hasRole.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, 'between', USER_ROLES.ONLINE_RETAILER) || this.hasRole.transform(this.getUser().roles, USER_ROLES.BROKER)) {
      
      return this.round((this.productDiscountedAmount(product, quantity, orders) / (taxRate/100 + 1)) * cwtRate); // original computation

      // wrong computation -> taxRate is already VAT
      /*
        var discountedAmount = this.productDiscountedAmount(product, quantity, orders);
        var netOfVat = discountedAmount / 1.12;
        return this.round((netOfVat / (taxRate/100 + 1)) * cwtRate);
      */
    }
    return  0;
  }

  productDiscountedAmount(product, quantity: number, orders) {
    return this.round((this.productSubTotal(product, undefined, quantity) - this.productDiscountSubTotal(product, quantity) - this.productVolumeDiscountSubTotal(product, quantity, orders)));
  }

  productTotalAmount(product, quantity: number, orders) {
    return this.round((this.productDiscountedAmount(product, quantity, orders) - this.productCWTSubTotal(product, quantity, orders)));
  }

  unitTax() {
    return (this.hasRole.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, 'between', USER_ROLES.ONLINE_RETAILER) || this.hasRole.transform(this.getUser().roles, USER_ROLES.BROKER)) ? (
      this.getUser() && this.getUser().merchantDetails && this.getUser().merchantDetails.withholdingTaxType &&
      this.getUser().merchantDetails.withholdingTaxType === WITHHOLDING_TAX_TYPE.CUSTOMER_PAYMENT &&
      WITHHOLDING_TAX_CODE['W' + this.getUser().merchantDetails.withholdingTaxCode] || 0
    ) : 0;
  }

  getUser() {
    return this.loopBackAuth.getToken() ? this.loopBackAuth.getCurrentUserData() : undefined;
  }

}
